import { useState } from 'react';
import { analytics } from '../../hooks/useAnalytics';
import { useAuthStore } from '../../store/authStore';
import { useTranslation } from '../../i18n';

interface CallButtonProps {
  occupantPhone: string | null;
  occupantId: string;
  spotLabel: string;
  variant?: 'icon' | 'button';
  disabled?: boolean;
}

export function CallButton({
  occupantPhone,
  occupantId,
  spotLabel,
  variant = 'button',
  disabled = false,
}: CallButtonProps) {
  const userId = useAuthStore((s) => s.user?.id);
  const [showError, setShowError] = useState(false);
  const { t } = useTranslation();

  if (!occupantPhone) {
    return null;
  }

  const handleCall = () => {
    if (!userId || !occupantPhone) return;

    try {
      // Track the call initiation with user IDs (no phone number)
      analytics.trackOccupantCallInitiated(userId, occupantId);

      // Initiate the call using tel: protocol
      window.location.href = `tel:${occupantPhone}`;
    } catch (error) {
      console.error('Failed to initiate call:', error);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={handleCall}
        disabled={disabled}
        title={`${t('call_occupant_of')} ${spotLabel}`}
        aria-label={`${t('call_occupant_of')} ${spotLabel}`}
        style={{
          background: 'none',
          border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: disabled ? '#ccc' : '#005bc0',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          opacity: disabled ? 0.5 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
          call
        </span>
      </button>
    );
  }

  return (
    <div style={{ marginBottom: '8px' }}>
      <button
        onClick={handleCall}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '12px 16px',
          borderRadius: '8px',
          border: 'none',
          background: disabled ? '#d1d5db' : 'linear-gradient(135deg, #16a34a, #15803d)',
          color: disabled ? '#6b7280' : '#ffffff',
          fontSize: '14px',
          fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          opacity: disabled ? 0.7 : 1,
          transition: 'all 0.15s',
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
          call
        </span>
        {t('call_occupant')}
      </button>
      {showError && (
        <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px', textAlign: 'center' }}>
          {t('unable_to_initiate_call')}
        </p>
      )}
    </div>
  );
}
